import { apiService } from "@/services/api";
import apiTamboUrl from "@/services/api";
import { createCrudStore } from "@/store/crudStore";

const base = createCrudStore("facturacion");

export default {
  ...base,
  state: {
    ...base.state,
    facturas: [],
    totalPages: 1,
    configuracion: null,
    configInfo: null,
    loadingFacturas: false,
    estadisticas: null,
  },
  getters: {
    ...base.getters,
    facturas(state) {
      return state.facturas;
    },
    totalPages(state) {
      return state.totalPages;
    },
    configuracion(state) {
      return state.configuracion;
    },
    configInfo(state) {
      return state.configInfo;
    },
    loadingFacturas(state) {
      return state.loadingFacturas;
    },
    tieneConfiguracion(state) {
      return !!state.configuracion;
    },
    estadisticas(state) {
      return state.estadisticas;
    },
  },
  mutations: {
    ...base.mutations,
    setFacturas(state, data) {
      state.facturas = data;
    },
    setTotalPages(state, val) {
      state.totalPages = val;
    },
    setConfiguracion(state, data) {
      state.configuracion = data;
    },
    setConfigInfo(state, data) {
      state.configInfo = data;
    },
    setLoadingFacturas(state, val) {
      state.loadingFacturas = val;
    },
    addFactura(state, factura) {
      state.facturas.unshift(factura);
    },
    setEstadisticas(state, data) {
      state.estadisticas = data;
    },
  },
  actions: {
    ...base.actions,

    // --- Config info (ligero, para el frontend) ---
    async getConfigInfo({ commit }) {
      try {
        const response = await apiService.getCustom("facturacion/config-info/");
        commit("setConfigInfo", response.data);
        return response.data;
      } catch (error) {
        if (error.response?.status === 404) {
          commit("setConfigInfo", null);
          return null;
        }
        throw error;
      }
    },

    // --- Configuración ARCA completa ---
    async getConfiguracion({ commit }) {
      try {
        const response = await apiService.getCustom(
          "facturacion/configuracion/"
        );
        commit("setConfiguracion", response.data);
        return response.data;
      } catch (error) {
        if (error.response?.status === 404) {
          commit("setConfiguracion", null);
          return null;
        }
        throw error;
      }
    },

    async guardarConfiguracion({ commit }, payload) {
      const response = await apiService.postCustom(
        "facturacion/configuracion/",
        payload
      );
      commit("setConfiguracion", response.data);
      return response.data;
    },

    async actualizarConfiguracion({ commit }, payload) {
      const response = await apiService.patchCustom(
        "facturacion/configuracion/",
        payload
      );
      commit("setConfiguracion", response.data);
      return response.data;
    },

    // --- Facturas (listar con paginación y filtros) ---
    async getFacturas({ commit }, params = {}) {
      commit("setLoadingFacturas", true);
      try {
        let url = "facturacion/facturas/";
        const queryParts = [];
        if (params.desde) queryParts.push(`desde=${params.desde}`);
        if (params.hasta) queryParts.push(`hasta=${params.hasta}`);
        if (params.estado) queryParts.push(`estado=${params.estado}`);
        if (params.limit) queryParts.push(`limit=${params.limit}`);
        if (params.offset !== undefined)
          queryParts.push(`offset=${params.offset}`);
        if (queryParts.length) url += "?" + queryParts.join("&");

        const response = await apiService.getCustom(url);
        // El backend puede devolver { facturas, total, pages } o un array directo
        if (Array.isArray(response.data)) {
          commit("setFacturas", response.data);
          commit("setTotalPages", 1);
        } else {
          commit("setFacturas", response.data.facturas || response.data);
          commit("setTotalPages", response.data.pages || 1);
        }
        return response.data;
      } finally {
        commit("setLoadingFacturas", false);
      }
    },

    async getFacturaDetalle(_, facturaId) {
      const response = await apiService.getCustom(
        `facturacion/facturas/${facturaId}/`
      );
      return response.data;
    },

    // --- Borradores ---
    async crearBorrador({ commit }, payload) {
      const response = await apiService.postCustom(
        "facturacion/borradores/",
        payload
      );
      commit("addFactura", response.data);
      return response.data;
    },

    async emitirBorrador(_, { facturaId, enviarEmail = true }) {
      const url = `facturacion/borradores/${facturaId}/emitir/?enviar_email=${enviarEmail}`;
      const response = await apiService.postCustom(url);
      return response.data;
    },

    async eliminarBorrador(_, facturaId) {
      const response = await apiService.deleteCustom(
        `facturacion/borradores/${facturaId}/`
      );
      return response.data;
    },

    // --- Emisión directa ---
    async emitirFactura({ commit }, { payload, enviarEmail = true }) {
      const url = `facturacion/emitir/?enviar_email=${enviarEmail}`;
      const response = await apiService.postCustom(url, payload);
      commit("addFactura", response.data);
      return response.data;
    },

    // --- Reintentar factura fallida ---
    async reintentarFactura(_, facturaId) {
      const response = await apiService.postCustom(
        `facturacion/facturas/${facturaId}/reintentar/`
      );
      return response.data;
    },

    // --- Reconsultar en ARCA ---
    async reconsultarFactura(_, facturaId) {
      const response = await apiService.postCustom(
        `facturacion/facturas/${facturaId}/reconsultar/`
      );
      return response.data;
    },

    // --- PDF ---
    async descargarPDF(_, facturaId) {
      const response = await apiService.getCustom(
        `facturacion/facturas/${facturaId}/pdf/`
      );
      return response;
    },

    // --- Email ---
    async reenviarEmail(_, facturaId) {
      const response = await apiService.postCustom(
        `facturacion/facturas/${facturaId}/reenviar-email/`
      );
      return response.data;
    },

    // --- WhatsApp ---
    async obtenerWhatsAppUrl(_, facturaId) {
      const response = await apiService.getCustom(
        `facturacion/facturas/${facturaId}/whatsapp-url/`
      );
      return response.data;
    },

    // --- Estadísticas ---
    async getEstadisticas({ commit }) {
      try {
        const response = await apiService.getCustom(
          "facturacion/estadisticas/"
        );
        commit("setEstadisticas", response.data);
        return response.data;
      } catch (error) {
        console.error("Error cargando estadísticas facturación:", error);
        throw error;
      }
    },

    // --- Puntos de venta ---
    async getPuntosVenta() {
      const response = await apiService.getCustom("facturacion/puntos-venta/");
      return response.data;
    },

    // --- Logs WS ---
    async getLogs(_, params = {}) {
      let url = "facturacion/logs/";
      const queryParts = [];
      if (params.factura_id) queryParts.push(`factura_id=${params.factura_id}`);
      if (params.limit) queryParts.push(`limit=${params.limit}`);
      if (params.offset !== undefined)
        queryParts.push(`offset=${params.offset}`);
      if (queryParts.length) url += "?" + queryParts.join("&");
      const response = await apiService.getCustom(url);
      return response.data;
    },

    async getLogDetalle(_, logId) {
      const response = await apiService.getCustom(`facturacion/logs/${logId}/`);
      return response.data;
    },

    // --- Test de conexión ARCA ---
    async testConexion() {
      const response = await apiService.getCustom(
        "facturacion/configuracion/test-conexion/"
      );
      return response.data;
    },

    // --- Generar CSR (descarga blob .csr) ---
    async generarCSR() {
      const response = await apiTamboUrl.post(
        "facturacion/configuracion/generar-csr/",
        {},
        { responseType: "blob" }
      );
      return response.data;
    },

    // --- Subir certificado digital (.crt + .key) ---
    async subirCertificado(_, formData) {
      const response = await apiTamboUrl.post(
        "facturacion/configuracion/certificado/",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    },
  },
};
